import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  Globe, Plus, Search, RefreshCw, Trash2, Edit, 
  Server, CheckCircle, XCircle, Copy, FileText,
  ChevronRight, AlertCircle, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SOA', 'PTR', 'SRV', 'CAA'];

const RECORD_TYPE_INFO = {
  'A': { description: 'IPv4 Address', placeholder: '192.168.1.1' },
  'AAAA': { description: 'IPv6 Address', placeholder: '2001:db8::1' },
  'CNAME': { description: 'Canonical Name (Alias)', placeholder: 'example.com.' },
  'MX': { description: 'Mail Exchange', placeholder: 'mail.example.com.' },
  'TXT': { description: 'Text Record', placeholder: 'v=spf1 include:_spf.google.com ~all' },
  'NS': { description: 'Name Server', placeholder: 'ns1.example.com.' },
  'SOA': { description: 'Start of Authority', placeholder: 'ns1.example.com. admin.example.com. 2024010101 3600 600 604800 86400' },
  'PTR': { description: 'Pointer (Reverse DNS)', placeholder: 'host.example.com.' },
  'SRV': { description: 'Service Record', placeholder: '10 5 5060 sip.example.com.' },
  'CAA': { description: 'Certification Authority', placeholder: '0 issue "letsencrypt.org"' }
};

export default function DNSManagement() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [records, setRecords] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [showZoneDialog, setShowZoneDialog] = useState(false);
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  
  // Form states
  const [zoneForm, setZoneForm] = useState({
    name: '',
    kind: 'Native',
    nameservers: 'ns1.yourisp.com.\nns2.yourisp.com.',
    customer_id: '',
    notes: ''
  });
  
  const [recordForm, setRecordForm] = useState({
    name: '',
    record_type: 'A',
    content: '',
    ttl: 3600,
    priority: null,
    disabled: false
  });
  
  const [syncing, setSyncing] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/dns/status`, { headers });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Error fetching DNS status:', error);
    }
  }, []);

  const fetchZones = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/dns/zones?search=${searchQuery}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setZones(data.zones);
      }
    } catch (error) {
      toast.error('Failed to fetch zones');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  const fetchRecords = useCallback(async (zoneId) => {
    try {
      const response = await fetch(`${API}/api/dns/zones/${zoneId}/records`, { headers });
      if (response.ok) {
        const data = await response.json();
        setRecords(data.records);
      }
    } catch (error) {
      toast.error('Failed to fetch records');
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/dns/templates`, { headers });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchZones();
    fetchTemplates();
  }, [fetchStatus, fetchZones, fetchTemplates]);

  useEffect(() => {
    if (selectedZone) {
      fetchRecords(selectedZone.zone_id);
    }
  }, [selectedZone, fetchRecords]);

  const handleCreateZone = async () => {
    if (!zoneForm.name) {
      toast.error('Please enter a domain name');
      return;
    }

    try {
      const response = await fetch(`${API}/api/dns/zones`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...zoneForm,
          nameservers: zoneForm.nameservers.split('\n').filter(ns => ns.trim())
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Zone created successfully');
        setShowZoneDialog(false);
        setZoneForm({ name: '', kind: 'Native', nameservers: 'ns1.yourisp.com.\nns2.yourisp.com.', customer_id: '', notes: '' });
        fetchZones();
        setSelectedZone(data.zone);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create zone');
      }
    } catch (error) {
      toast.error('Failed to create zone');
    }
  };

  const handleCreateRecord = async () => {
    if (!recordForm.name || !recordForm.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    const url = editingRecord 
      ? `${API}/api/dns/zones/${selectedZone.zone_id}/records/${editingRecord.record_id}`
      : `${API}/api/dns/zones/${selectedZone.zone_id}/records`;

    try {
      const response = await fetch(url, {
        method: editingRecord ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(recordForm)
      });

      if (response.ok) {
        toast.success(editingRecord ? 'Record updated' : 'Record created');
        setShowRecordDialog(false);
        setEditingRecord(null);
        setRecordForm({ name: '', record_type: 'A', content: '', ttl: 3600, priority: null, disabled: false });
        fetchRecords(selectedZone.zone_id);
        fetchZones();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save record');
      }
    } catch (error) {
      toast.error('Failed to save record');
    }
  };

  const handleDelete = async () => {
    if (!deletingItem) return;

    const isZone = deletingItem.type === 'zone';
    const url = isZone 
      ? `${API}/api/dns/zones/${deletingItem.id}`
      : `${API}/api/dns/zones/${selectedZone.zone_id}/records/${deletingItem.id}`;

    try {
      const response = await fetch(url, { method: 'DELETE', headers });
      if (response.ok) {
        toast.success(isZone ? 'Zone deleted' : 'Record deleted');
        if (isZone) {
          setSelectedZone(null);
          setRecords([]);
          fetchZones();
        } else {
          fetchRecords(selectedZone.zone_id);
          fetchZones();
        }
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setShowDeleteDialog(false);
      setDeletingItem(null);
    }
  };

  const handleSyncZone = async () => {
    if (!selectedZone) return;
    
    setSyncing(true);
    try {
      const response = await fetch(`${API}/api/dns/zones/${selectedZone.zone_id}/sync`, {
        method: 'POST',
        headers
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchZones();
        fetchRecords(selectedZone.zone_id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Sync failed');
      }
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const applyTemplate = async (template) => {
    if (!selectedZone) {
      toast.error('Please select a zone first');
      return;
    }

    try {
      const response = await fetch(`${API}/api/dns/zones/${selectedZone.zone_id}/records/bulk`, {
        method: 'POST',
        headers,
        body: JSON.stringify(template.records)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Created ${data.created} records from template`);
        fetchRecords(selectedZone.zone_id);
        fetchZones();
      } else {
        toast.error('Failed to apply template');
      }
    } catch (error) {
      toast.error('Failed to apply template');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const openEditRecord = (record) => {
    setEditingRecord(record);
    setRecordForm({
      name: record.display_name,
      record_type: record.record_type,
      content: record.content,
      ttl: record.ttl,
      priority: record.priority,
      disabled: record.disabled
    });
    setShowRecordDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">DNS Management</h1>
          <p className="text-gray-500">Manage DNS zones and records for customer domains</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={status?.connection?.connected ? "default" : "destructive"} className="gap-1">
            {status?.connection?.connected ? (
              <><CheckCircle className="w-3 h-3" /> PowerDNS Connected</>
            ) : (
              <><XCircle className="w-3 h-3" /> PowerDNS Offline</>
            )}
          </Badge>
          <Button onClick={() => setShowZoneDialog(true)}>
            <Plus className="w-4 h-4 mr-2" /> Add Zone
          </Button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Globe className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Zones</p>
                <p className="text-2xl font-bold">{zones.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Records</p>
                <p className="text-2xl font-bold">{zones.reduce((sum, z) => sum + (z.record_count || 0), 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Server className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Synced Zones</p>
                <p className="text-2xl font-bold">{zones.filter(z => z.pdns_synced).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending Sync</p>
                <p className="text-2xl font-bold">{zones.filter(z => !z.pdns_synced).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Zones List */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">DNS Zones</CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search zones..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[500px] overflow-y-auto">
              {zones.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <Globe className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No zones found</p>
                  <Button variant="link" onClick={() => setShowZoneDialog(true)}>
                    Create your first zone
                  </Button>
                </div>
              ) : (
                zones.map((zone) => (
                  <div
                    key={zone.zone_id}
                    onClick={() => setSelectedZone(zone)}
                    className={`flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 border-b ${
                      selectedZone?.zone_id === zone.zone_id ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="font-medium truncate">{zone.name}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {zone.record_count || 0} records
                        </Badge>
                        {zone.pdns_synced ? (
                          <Badge variant="default" className="text-xs bg-green-500">Synced</Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">Local</Badge>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Zone Details & Records */}
        <Card className="lg:col-span-2">
          {selectedZone ? (
            <>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="w-5 h-5 text-blue-500" />
                      {selectedZone.name}
                    </CardTitle>
                    <CardDescription>
                      {selectedZone.record_count || 0} records • Created {new Date(selectedZone.created_at).toLocaleDateString()}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleSyncZone} disabled={syncing}>
                      {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      <span className="ml-2">Sync to DNS</span>
                    </Button>
                    <Button size="sm" onClick={() => {
                      setEditingRecord(null);
                      setRecordForm({ name: '', record_type: 'A', content: '', ttl: 3600, priority: null, disabled: false });
                      setShowRecordDialog(true);
                    }}>
                      <Plus className="w-4 h-4 mr-1" /> Add Record
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => {
                        setDeletingItem({ type: 'zone', id: selectedZone.zone_id, name: selectedZone.name });
                        setShowDeleteDialog(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="records">
                  <TabsList>
                    <TabsTrigger value="records">Records</TabsTrigger>
                    <TabsTrigger value="templates">Templates</TabsTrigger>
                    <TabsTrigger value="info">Zone Info</TabsTrigger>
                  </TabsList>

                  <TabsContent value="records" className="mt-4">
                    {records.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No records found</p>
                        <p className="text-sm">Add records manually or use a template</p>
                      </div>
                    ) : (
                      <div className="rounded-md border overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Name</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead>Content</TableHead>
                              <TableHead>TTL</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="w-[100px]">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {records.map((record) => (
                              <TableRow key={record.record_id}>
                                <TableCell className="font-mono text-sm">
                                  {record.display_name || '@'}
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline">{record.record_type}</Badge>
                                </TableCell>
                                <TableCell className="max-w-[200px] truncate font-mono text-sm">
                                  <div className="flex items-center gap-1">
                                    <span className="truncate">{record.content}</span>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-6 w-6 p-0"
                                      onClick={() => copyToClipboard(record.content)}
                                    >
                                      <Copy className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </TableCell>
                                <TableCell>{record.ttl}s</TableCell>
                                <TableCell>
                                  {record.disabled ? (
                                    <Badge variant="secondary">Disabled</Badge>
                                  ) : record.pdns_synced ? (
                                    <Badge className="bg-green-500">Active</Badge>
                                  ) : (
                                    <Badge variant="outline">Local</Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-1">
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => openEditRecord(record)}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => {
                                        setDeletingItem({ type: 'record', id: record.record_id, name: `${record.display_name} ${record.record_type}` });
                                        setShowDeleteDialog(true);
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="templates" className="mt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {templates.map((template, idx) => (
                        <Card key={idx} className="cursor-pointer hover:border-blue-300" onClick={() => applyTemplate(template)}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm">{template.name}</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-1">
                              {template.records.slice(0, 3).map((r, i) => (
                                <div key={i} className="text-xs text-gray-500 font-mono">
                                  {r.name} {r.record_type} → {r.content.substring(0, 30)}...
                                </div>
                              ))}
                              {template.records.length > 3 && (
                                <div className="text-xs text-gray-400">+{template.records.length - 3} more records</div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="info" className="mt-4">
                    <div className="space-y-4">
                      <div>
                        <Label className="text-gray-500">Zone Type</Label>
                        <p className="font-medium">{selectedZone.kind}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Nameservers</Label>
                        <div className="space-y-1 mt-1">
                          {selectedZone.nameservers?.map((ns, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <code className="bg-gray-100 px-2 py-1 rounded text-sm">{ns}</code>
                              <Button variant="ghost" size="sm" onClick={() => copyToClipboard(ns)}>
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                      {selectedZone.notes && (
                        <div>
                          <Label className="text-gray-500">Notes</Label>
                          <p>{selectedZone.notes}</p>
                        </div>
                      )}
                      <div>
                        <Label className="text-gray-500">PowerDNS Status</Label>
                        <p>{selectedZone.pdns_synced ? 'Synced' : 'Not synced'}</p>
                        {selectedZone.pdns_error && (
                          <p className="text-red-500 text-sm mt-1">{selectedZone.pdns_error}</p>
                        )}
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </>
          ) : (
            <CardContent className="flex flex-col items-center justify-center h-[400px] text-gray-500">
              <Globe className="w-16 h-16 mb-4 text-gray-300" />
              <p className="text-lg font-medium">Select a zone</p>
              <p className="text-sm">Choose a zone from the list to view and manage records</p>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Create Zone Dialog */}
      <Dialog open={showZoneDialog} onOpenChange={setShowZoneDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create DNS Zone</DialogTitle>
            <DialogDescription>Add a new domain to manage DNS records</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Domain Name *</Label>
              <Input
                placeholder="example.com"
                value={zoneForm.name}
                onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Zone Type</Label>
              <Select value={zoneForm.kind} onValueChange={(v) => setZoneForm({ ...zoneForm, kind: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Native">Native (Recommended)</SelectItem>
                  <SelectItem value="Master">Master</SelectItem>
                  <SelectItem value="Slave">Slave</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nameservers (one per line)</Label>
              <Textarea
                placeholder="ns1.yourisp.com.&#10;ns2.yourisp.com."
                value={zoneForm.nameservers}
                onChange={(e) => setZoneForm({ ...zoneForm, nameservers: e.target.value })}
                rows={3}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                placeholder="Optional notes about this zone"
                value={zoneForm.notes}
                onChange={(e) => setZoneForm({ ...zoneForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowZoneDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateZone}>Create Zone</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Record Dialog */}
      <Dialog open={showRecordDialog} onOpenChange={setShowRecordDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingRecord ? 'Edit Record' : 'Add DNS Record'}</DialogTitle>
            <DialogDescription>
              {selectedZone?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Record Name *</Label>
              <Input
                placeholder="@ for root, www, mail, etc."
                value={recordForm.name}
                onChange={(e) => setRecordForm({ ...recordForm, name: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">Use @ for the root domain</p>
            </div>
            <div>
              <Label>Record Type</Label>
              <Select 
                value={recordForm.record_type} 
                onValueChange={(v) => setRecordForm({ ...recordForm, record_type: v })}
                disabled={!!editingRecord}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RECORD_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type} - {RECORD_TYPE_INFO[type]?.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Content *</Label>
              <Input
                placeholder={RECORD_TYPE_INFO[recordForm.record_type]?.placeholder || 'Record value'}
                value={recordForm.content}
                onChange={(e) => setRecordForm({ ...recordForm, content: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>TTL (seconds)</Label>
                <Input
                  type="number"
                  value={recordForm.ttl}
                  onChange={(e) => setRecordForm({ ...recordForm, ttl: parseInt(e.target.value) || 3600 })}
                />
              </div>
              {(recordForm.record_type === 'MX' || recordForm.record_type === 'SRV') && (
                <div>
                  <Label>Priority</Label>
                  <Input
                    type="number"
                    value={recordForm.priority || ''}
                    onChange={(e) => setRecordForm({ ...recordForm, priority: parseInt(e.target.value) || null })}
                  />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={recordForm.disabled}
                onCheckedChange={(checked) => setRecordForm({ ...recordForm, disabled: checked })}
              />
              <Label>Disabled</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowRecordDialog(false);
              setEditingRecord(null);
            }}>Cancel</Button>
            <Button onClick={handleCreateRecord}>
              {editingRecord ? 'Update Record' : 'Add Record'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {deletingItem?.type === 'zone' ? 'zone' : 'record'} "{deletingItem?.name}"?
              {deletingItem?.type === 'zone' && ' This will also delete all associated records.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
